import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { BrandMark } from '../components/Brand';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

// Minimal, public-facing sign-in. No credentials of any kind are rendered here.
export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect already-authenticated users away from the login page (in an effect,
  // never during render, to avoid router state-update warnings).
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);


  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedIn = await login(email, password);
      const firstName = (loggedIn?.name || '').trim().split(/\s+/)[0];
      toast.success(firstName ? `Welcome, ${firstName}` : 'Welcome');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-6 py-12 text-foreground">
      {/* subtle ambient backdrop (decorative only) */}
      <div className="pointer-events-none absolute inset-0 blueprint-grid opacity-[0.35]" aria-hidden />
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[hsl(var(--copper)/0.08)] blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[hsl(var(--teal)/0.08)] blur-3xl" aria-hidden />

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative w-full max-w-sm"
        data-testid="login-card"
      >
        <div className="erak-card p-7 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <BrandMark size="lg" />
          </div>

          <div className="mt-7">
            <h2 className="font-display text-2xl font-bold">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Sign in to your account.</p>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@erak" autoComplete="username" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-muted-foreground"><Checkbox defaultChecked /> Remember me</label>
              <button type="button" className="text-[hsl(var(--teal))] hover:underline" onClick={() => toast.info('Contact your admin to reset your password.')}>Forgot password?</button>
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full">
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}Sign in
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
